import {
  Box,
  Button,
  ListItemIcon,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import {
  MouseEvent,
  Ref,
  ReactElement,
  cloneElement,
  forwardRef,
  isValidElement,
  memo,
  useImperativeHandle,
  useState,
} from "react";
import { NestedMenuItem } from "mui-nested-menu";
import { ContextMenuAction } from "../../types/contextMenuAction";
import { ContextMenuState } from "../../types/contextMenuState.type";
import { labelButtonStyle } from "./PopupMenu";

/**
 * Constant that anchors the context menu away from the cursor using its size
 */
const contextMenuMouseCursorOffset = {
  x: 2,
  y: -6,
};

/**
 * Helper function to detect if a component is a NestedMenuItem
 * NestedMenuItems need special handling - no Box onClick to allow hover behavior
 */
const isNestedMenuItem = (element: ReactElement): boolean =>
  element.type === NestedMenuItem;

type Props<T> = {
  actions: (row?: T) => ContextMenuAction[];
};

export type ContextMenuRef<T> = {
  openContextMenu: (event: MouseEvent, tableRow?: T) => void;
  closeContextMenu: () => void;
};

/** A context menu used in tables and sidebars with shipments. */
const ContextMenuDropdown = <T,>(
  { actions }: Props<T>,
  ref?: Ref<ContextMenuRef<T>>
) => {
  const theme = useTheme();
  const [contextActions, setContextActions] = useState<ContextMenuAction[]>([]);
  const [menuPosition, setMenuPosition] = useState<ContextMenuState>(null);

  const handleContextMenu = (event: MouseEvent, tableRow?: T) => {
    event.preventDefault();
    const menuActions = actions(tableRow || undefined);
    if (menuActions.length <= 1) {
      setContextActions(menuActions);
      return;
    }

    setMenuPosition(
      menuPosition === null
        ? {
            mouseX: event.clientX + contextMenuMouseCursorOffset.x,
            mouseY: event.clientY + contextMenuMouseCursorOffset.y,
          }
        : null
    );
    setContextActions(menuActions);
  };

  const handleClose = () => {
    setMenuPosition(null);
  };

  useImperativeHandle(ref, () => ({
    openContextMenu: handleContextMenu,
    closeContextMenu: handleClose,
  }));

  return (
    <Menu
      open={menuPosition !== null}
      onClose={handleClose}
      anchorReference="anchorPosition"
      anchorPosition={
        menuPosition !== null
          ? { top: menuPosition.mouseY, left: menuPosition.mouseX }
          : undefined
      }
    >
      {contextActions.map((option) =>
        option.itemComponent ? (
          <Box
            key={option.label}
            onClick={
              isValidElement(option.itemComponent) &&
              isNestedMenuItem(option.itemComponent)
                ? undefined
                : (event) => {
                    option.handleClick?.(event);
                    if (!option.keepOpenAfterClick) {
                      handleClose();
                    }
                  }
            }
          >
            {isValidElement(option.itemComponent)
              ? cloneElement(option.itemComponent, {
                  ...option.itemComponent.props,
                  handleClose,
                })
              : option.itemComponent}
          </Box>
        ) : (
          <MenuItem
            disabled={option.disabled}
            key={option.label}
            onClick={(event) => {
              option.handleClick?.(event);
              if (!option.keepOpenAfterClick) {
                handleClose();
              }
            }}
            sx={{
              "&:hover": {
                background: `${theme.palette.grey[50]} !important`,
              },
              "&:focus": {
                background: "inherit",
              },
            }}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <Button sx={labelButtonStyle} disableRipple>
              {option.label}
            </Button>
          </MenuItem>
        )
      )}
    </Menu>
  );
};

ContextMenuDropdown.displayName = "ContextMenuDropdown";
export default memo(forwardRef(ContextMenuDropdown)) as <T>(
  props: Props<T> & { ref: Ref<ContextMenuRef<T>> }
) => React.JSX.Element;
